import dayjs from "dayjs";

import { dateRangeOptions } from "../constants";
import {
  DateRange,
  DateRangeValue,
  FormatDateUtil,
  IRound,
  NumericFormatUtil,
  PreviewDateRangeUtil
} from "../store/types";

export const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export const formatDate: FormatDateUtil = ({ date, format = "MM/DD/YYYY", readFormatHint, empty = "-" }): string => {
  if (!date) return empty;

  try {
    // Convert format string to dayjs format once
    let dayjsFormat = format.replace(/dd/g, "DD").replace(/yyyy/g, "YYYY");

    // Handle time formats - preserve mm for minutes in time contexts
    // First, protect time patterns by temporarily replacing them
    const timePatterns = dayjsFormat.match(/([Hh]+):mm/g) || [];
    let protectedFormat = dayjsFormat;
    timePatterns.forEach((pattern, index) => {
      protectedFormat = protectedFormat.replace(pattern, `__TIME_${index}__`);
    });

    // Convert single 'm' to 'mm' for minutes (but not if it's part of 'mm' or preceded by 'H')
    protectedFormat = protectedFormat.replace(/(?<!H)m(?![m])/g, "mm");

    // Convert remaining mm to MM (months)
    protectedFormat = protectedFormat.replace(/mm/g, "MM");

    // Restore time patterns
    timePatterns.forEach((pattern, index) => {
      protectedFormat = protectedFormat.replace(`__TIME_${index}__`, pattern);
    });

    dayjsFormat = protectedFormat;

    // Check if it's a Unix timestamp (exactly 10 digits)
    const dateString = String(date);
    if (/^\d{10}$/.test(dateString)) {
      return dayjs.unix(Number(dateString)).format(dayjsFormat);
    }

    // Handle ISO dates and other formats
    return dayjs(date, readFormatHint).format(dayjsFormat);
  } catch (error) {
    console.warn("Date formatting error:", error, "for date:", date);
    return empty;
  }
};

export const previewDateRange: PreviewDateRangeUtil = ({ from, to, value, format = "MM/DD/YYYY" }): string => {
  if (value === "dateRange") {
    if (from && to) {
      return formatDate({ date: from, format }) + " - " + formatDate({ date: to, format });
    } else if (to) {
      return "To " + formatDate({ date: to, format });
    } else if (from) {
      return "From " + formatDate({ date: from, format });
    } else {
      return "All Times";
    }
  } else if (value === "selectYear" && from) {
    return dayjs(from).format("YYYY").toString();
  }

  return dateRangeOptions.find((item) => item.value === value)?.text || value;
};

export const convertDateRange = ({ from, to, value }: DateRangeValue<string>): DateRange<string> => {
  const isoMiddayDates: DateRange<string> = {};

  if (value === "ytd") {
    isoMiddayDates.from = dayjs().utc().startOf("year").format("YYYY-MM-DD");
    isoMiddayDates.to = "today";
  } else if (value === "fromInception") {
    delete isoMiddayDates.from;
    isoMiddayDates.to = "today";
  } else if (value === "lastYear") {
    const lastYear = (new Date().getFullYear() - 1).toString();
    isoMiddayDates.from = dayjs(new Date(lastYear)).utc().format("YYYY-MM-DD");
    isoMiddayDates.to = dayjs(new Date(lastYear + "-12-31"))
      .utc()
      .format("YYYY-MM-DD");
  } else if (value === "last3Years") {
    isoMiddayDates.from = dayjs().utc().subtract(3, "years").format("YYYY-MM-DD");
    isoMiddayDates.to = "today";
  } else if (value === "last5Years") {
    isoMiddayDates.from = dayjs().utc().subtract(5, "years").format("YYYY-MM-DD");
    isoMiddayDates.to = "today";
  } else if (value === "inceptionToLastNAV") {
    delete isoMiddayDates.from;
    isoMiddayDates.to = "lastNav";
  } else {
    if (to === "today") {
      isoMiddayDates.to = "today";
    }
    if (from) {
      isoMiddayDates.from = dayjs(from).format("YYYY-MM-DD");
    }
    if (to && to !== "today") {
      isoMiddayDates.to = dayjs(to).format("YYYY-MM-DD");
    }
  }

  return isoMiddayDates;
};

export const dateFormatter = (date: string) => {
  const incomingDate = dayjs(date);
  const currentMoment = dayjs();
  const yesterday = dayjs().subtract(1, "days").startOf("day");

  const isToday = currentMoment.isSame(incomingDate, "d");
  const isYesterday = yesterday.isSame(incomingDate, "d");
  if (isToday) {
    return "Today";
  } else if (isYesterday) {
    return "Yesterday";
  } else {
    return incomingDate.format("MMM Do YYYY");
  }
};

export const toFormData = (data: any) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    // Array handle
    if (data[key] instanceof Array) {
      if (!data[key].length) {
        return formData.append(`${key}[0]`, "");
      }
      for (let i = 0; i < data[key].length; i++) {
        formData.append(`${key}[${i}]`, data[key][i]);
      }
      // Cut after loop
      return;
    }
    formData.append(key, data[key]);
  });
  return formData;
};

export function JSON_to_URLEncoded(element: any, key?: any, args?: any) {
  const list = args || [];
  if (typeof element == "object") {
    for (const idx in element) JSON_to_URLEncoded(element[idx], key ? key + "[" + idx + "]" : idx, list);
  } else {
    list.push(key + "=" + encodeURIComponent(element));
  }
  return list.join("&");
}

// mutable
export const clearObject = (obj: { [key: string]: any }) => {
  for (const propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined || obj[propName] === "") {
      delete obj[propName];
    }
  }
};

export const getIdsObjects = (data: any[]): Array<{ id: number }> => {
  return data.reduce((acc, element) => {
    return [...acc, { id: element.id }];
  }, []);
};

export const allowOnlyNum = (value: string): string => {
  const isNormal = /^\d*\.?\d*$/.test(value);
  return isNormal ? value : value.slice(0, -1);
};

interface IGetFullName {
  first_name: string;
  last_name: string;
}

export const getFullName = (user: IGetFullName): string => {
  if (!user) {
    return "-";
  }

  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  } else if (user.first_name) {
    return user.first_name;
  } else if (user.last_name) {
    return user.last_name;
  } else {
    return "No Name";
  }
};

export const copyToClipboard = (content: string) => {
  const el = document.createElement("textarea");
  el.value = content;
  navigator.clipboard.writeText(el.value);
};

export const pickBy = <T>(obj: Record<string, T>, filter: (key: string) => boolean) => {
  return Object.keys(obj)
    .filter((key) => filter(key))
    .reduce<Record<string, T>>((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
};

export const getRoundSumm = (round: IRound): number => {
  return round.deal_commitments.reduce((accumulator, commitment) => {
    return accumulator + Number(commitment.amount);
  }, 0);
};

export const isYesterday = (date: string | null): boolean => {
  if (!date) return false;

  const oneDay = 60 * 60 * 24 * 1000;
  const current = new Date().getTime();
  const checkDate = new Date(date).getTime();

  const difference = current - checkDate;

  return difference <= oneDay;
};

export const normalizeGroupByCharacteristic = (characteristic: string) => {
  if (characteristic === "Asset Class") {
    return "assetClass";
  }
  if (characteristic === "Sub Asset Class") {
    return "subAssetClass";
  }
  return characteristic;
};

/** Prefix on chart `groupBy` when binning by measure/calculated field (Characteristics use normalized keys only). */
export const CHART_BIN_BY_MEASURE_PREFIX = "binBy:" as const;

export const formatNumberWithoutRounding = (number: number, decimal: number): string => {
  const [integerPart, fractionalPart = ""] = number.toString().split(".");
  const truncatedFraction = `${fractionalPart}${"0".repeat(decimal)}`.slice(0, decimal);
  return `${Number(integerPart).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}${truncatedFraction && "." + truncatedFraction}`;
};

export const numericToAbbreviated: NumericFormatUtil = ({ value, format = {} }) => {
  if (!format) return typeof value === "string" ? value : value.toString();
  const { abbreviated, decimal } = format;
  if (decimal === undefined) return typeof value === "string" ? value : value.toString();
  if (value === null || value === undefined || value === "-") return "-";

  // Handle percentage and currency formats
  let number: number;
  let prefix = "";
  let suffix = "";
  let isNegative = false;

  if (typeof value === "string") {
    // Extract any currency symbols or % signs
    const matches = value.match(/^(%)?([₪$€£])?-?([\d,]+\.?\d*)\s*(%)?$/);
    if (matches) {
      const hasPercentPrefix = matches[1];
      prefix = matches[2] || "";
      suffix = hasPercentPrefix ? "%" : matches[4] || "";
      number = parseFloat(matches[3].replace(/,/g, ""));
      isNegative = value.includes("-");
    } else {
      number = parseFloat(value.replace(/[^0-9.-]/g, ""));
      isNegative = number < 0;
    }
  } else {
    number = value;
    isNegative = number < 0;
  }

  if (isNaN(number)) return value.toString();

  // Use absolute value for calculations
  number = Math.abs(number);

  if (abbreviated) {
    if (number < 500) {
      return `${prefix}${isNegative ? "-" : ""}${formatNumberWithoutRounding(number, decimal)}${suffix}`;
    }

    const suffixes = ["", "K", "M", "B", "T", "Q"];
    const tier = Math.floor(Math.log10(number) / 3);

    const abbreviationSuffix = suffixes[tier];
    const scale = Math.pow(10, tier * 3);
    const scaledValue = number / scale;

    return `${prefix}${isNegative ? "-" : ""}${formatNumberWithoutRounding(scaledValue, decimal)}${abbreviationSuffix}${suffix}`;
  } else {
    return `${prefix}${isNegative ? "-" : ""}${formatNumberWithoutRounding(number, decimal)}${suffix}`;
  }
};

export const camelCaseToTitle = (str: string) => {
  if (!str) return str;

  // Check if string is already in the desired format (starts with capital letter)
  if (/^[A-Z][a-z]+( [A-Z][a-z]+)*$/.test(str)) {
    return str;
  }

  // Handle snake_case
  if (str.includes("_")) {
    return str
      .split("_")
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()
      )
      .join(" ");
  }

  // Handle camelCase
  if (/[a-z][A-Z]/.test(str)) {
    return str
      .replace(/([A-Z])/g, " $1")
      .split(" ")
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()
      )
      .join(" ");
  }

  // Handle single word or simple cases
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getLastCompletedMonthRange = (): string => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
  const monthName = monthFormatter.format(lastMonth);
  const firstDay = 1;
  const lastDay = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate();
  return `${monthName} ${firstDay}–${monthName} ${lastDay}`;
};

export const getLastCompletedQuarterRange = (): string => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11 (Jan = 0, Dec = 11)
  const currentYear = now.getFullYear();

  // Determine the last completed quarter
  let quarterStartMonth: number;
  let quarterEndMonth: number;
  let quarterYear: number;

  if (currentMonth < 3) {
    // Jan, Feb, Mar -> last quarter is Q4 of previous year (Oct-Dec)
    quarterStartMonth = 9; // October (0-indexed)
    quarterEndMonth = 11; // December (0-indexed)
    quarterYear = currentYear - 1;
  } else if (currentMonth < 6) {
    // Apr, May, Jun -> last quarter is Q1 (Jan-Mar)
    quarterStartMonth = 0; // January
    quarterEndMonth = 2; // March
    quarterYear = currentYear;
  } else if (currentMonth < 9) {
    // Jul, Aug, Sep -> last quarter is Q2 (Apr-Jun)
    quarterStartMonth = 3; // April
    quarterEndMonth = 5; // June
    quarterYear = currentYear;
  } else {
    // Oct, Nov, Dec -> last quarter is Q3 (Jul-Sep)
    quarterStartMonth = 6; // July
    quarterEndMonth = 8; // September
    quarterYear = currentYear;
  }

  const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
  const startMonthName = monthFormatter.format(new Date(quarterYear, quarterStartMonth, 1));
  const endMonthName = monthFormatter.format(new Date(quarterYear, quarterEndMonth, 1));
  const startDay = 1;
  const endDay = new Date(quarterYear, quarterEndMonth + 1, 0).getDate();

  return `${startMonthName} ${startDay}–${endMonthName} ${endDay}`;
};
